import React, { useState, useEffect } from "react";
import styled from "styled-components";
import LoadingInComp from "./LoadingInComp";

interface ZoomableImageProps {
  imageUrl: string; // e.g. the S3 URL
  scale: number;
  setScale: (scale: number) => void;
  isShiftPressed: boolean;
}

const ZoomableImage: React.FC<ZoomableImageProps> = ({ imageUrl, scale, setScale, isShiftPressed }) => {
  const [originalDimensions, setOriginalDimensions] = useState<{ width: number; height: number } | null>(null);
  const [scaledDimensions, setScaledDimensions] = useState<{ width: number; height: number } | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      const ratio = img.naturalWidth / img.naturalHeight;
      const imgLogerSide = Math.max(img.naturalWidth, img.naturalHeight);
      const windowLogerSide = Math.max(window.innerWidth, window.innerHeight);

      if (imgLogerSide > windowLogerSide) {
        const newWidth = Math.round((ratio > 1 ? window.innerWidth : window.innerHeight * ratio) * 10) / 10;
        const newHeight = Math.round((ratio > 1 ? window.innerWidth * (1 / ratio) : window.innerHeight) * 10) / 10;

        setOriginalDimensions({
          width: newWidth,
          height: newHeight
        });
        setScaledDimensions({
          width: newWidth,
          height: newHeight
        });
      } else {
        setOriginalDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
        setScaledDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      }
    };
  }, []);

  useEffect(() => {
    if (!originalDimensions) return;
    const newWidth = Math.round(originalDimensions.width * scale);
    const newHeight = Math.round(originalDimensions.height * scale);
    const widthDiffer = newWidth - (scaledDimensions?.width || 0);
    const heightDiffer = newHeight - (scaledDimensions?.height || 0);

    setScaledDimensions({
      width: newWidth,
      height: newHeight,
    })

    //둘다 끌어내려야해
    if (newWidth > window.innerWidth && newHeight > window.innerHeight) {
      // console.log('both new bigger than window');
      //커지는 만큼 끌고와야해. 
      setPosition({
        top: position.top - Math.round(heightDiffer / 2),
        left: position.left - Math.round(widthDiffer / 2),
      })
      return;
    }

    if (newWidth > window.innerWidth) {
      // console.log('newWidth > window.innerWidth');
      setPosition({
        top: 0,
        left: position.left - widthDiffer / 2,
      })
      return;
    }
    if (newHeight > window.innerHeight) {
      // console.log('newHeight > window.innerHeight');
      setPosition({
        top: position.top - heightDiffer / 2,
        left: 0,
      })
      return;
    }
    if (newWidth < window.innerWidth && newHeight < window.innerHeight) {
      //커지는 만큼 끌고와야해. 
      setPosition({
        top: 0,
        left: 0,
      })
      return;
    }

  }, [scale]);

  const handleZoomIn = () => {
    if (scale < 3) {
      setScale(scale + 0.2);
    }
  }

  const handleZoomOut = () => {
    if (scale > 0.6) {
      setScale(scale - 0.2);
    }
  }

  const handleImgClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (e.shiftKey) {
      handleZoomOut();
    } else {
      handleZoomIn();
    }
  }

  if (!originalDimensions || !scaledDimensions) {
    return <LoadingInComp isLoading={true} />
  }

  return (
    <ZoomableImg
      src={imageUrl}
      $position={position}
      $originWidth={scaledDimensions.width}
      $originHeight={scaledDimensions.height}
      $scale={scale}
      $isShiftPressed={isShiftPressed}
      onClick={handleImgClick}
    />
  );
};


const ZoomableImg = styled.img<{ $scale: number, $originWidth: number, $originHeight: number, $isShiftPressed: boolean, $position: { top: number, left: number } }>`
  position: absolute;
  top: calc(50% - ${({ $position }) => $position.top}px);
  left: calc(50% - ${({ $position }) => $position.left}px);
  transform: translate(-50%, -50%);


  width: ${({ $originWidth }) => $originWidth}px;
  height: ${({ $originHeight }) => $originHeight}px;
  
  cursor: ${({ $isShiftPressed }) => $isShiftPressed ? 'zoom-out' : 'zoom-in'};

  transition: all 0.4s ease-in-out;
  z-index: 5;
`;


export default ZoomableImage;